import { HOST, } from "common/constants";
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  CardContent,
  Grid,
  Divider,
  Box,
  CircularProgress,
  Stack,
  Paper,
  Breadcrumbs,
} from "@mui/material";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BackButton } from "components/UI/BackButton";

// Иконки
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Facebook, Instagram, ChevronRight } from "@mui/icons-material";

const MunitsipalitetId = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [district, setDistrict] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDistrictDetail = async () => {
      try {
        const response = await axios.get(
          `${HOST}/api/v1/municipalities/${id}/`
        );
        setDistrict(response.data);
      } catch (error) {
        console.error("Ошибка при загрузке:", error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchDistrictDetail();
  }, [id]);

  const getLangVal = (obj, field) => {
    if (!obj) return "";
    const currentLang = i18n.language;
    return obj[`${field}_${currentLang}`] || obj[`${field}_ru`] || obj[field];
  };

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress thickness={3} size={50} />
      </Box>
    );

  if (!district)
    return (
      <Container sx={{ mt: 10 }}>
        <Typography>{t("dataNotFound")}</Typography>
      </Container>
    );

  return (
    <Box sx={{ bgcolor: "#f5f7fa", minHeight: "100vh", pb: 10 }}>
      {/* Шапка */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          pt: 4,
          pb: 4,
          borderBottom: "1px solid #e0e0e0",
          mb: 4,
        }}
      >
        <Container>
          <BackButton />

          <Breadcrumbs
            separator={<ChevronRight fontSize="small" />}
            sx={{ mb: 1 }}
          >
            <Link
              to="/organs"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {t("msuBodies")}
            </Link>
            <Typography color="text.primary">
              {getLangVal(district, "name")}
            </Typography>
          </Breadcrumbs>

          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: "#1a237e", mt: 1 }}
          >
            {getLangVal(district, "name")}
          </Typography>
        </Container>
      </Box>

      <Container>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #e0e0e0",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                mb: 4,
              }}
            >
              {/* Адрес */}
              <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
                {t("address_mun")}:{" "}
                <Box component="span" sx={{ fontWeight: 600, ml: 1 }}>
                  {getLangVal(district, "address")}
                </Box>
              </Typography>

              {/* Ссылка на сайт */}
              {district.link && (
                <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
                  {t("link_mun")}:{" "}
                  <Box
                    component="a"
                    href={district.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontWeight: 600,
                      ml: 1,
                      color: "#1976d2",
                      textDecoration: "none",
                    }}
                  >
                    {district.link}
                  </Box>
                </Typography>
              )}

              {/* Номер телефона */}
              {district.phone_number && (
                <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
                  {t("phone_number_mun")}:{" "}
                  <Box
                    component="a"
                    href={`tel:${district.phone_number}`}
                    sx={{
                      fontWeight: 600,
                      ml: 1,
                      color: "#1976d2",
                      textDecoration: "none",
                    }}
                  >
                    {district.phone_number}
                  </Box>
                </Typography>
              )}

              <Typography sx={{ fontSize: "16px", fontWeight: 600, marginTop: 2 }}>
                {t("social_page")}:
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {district.facebook && (
                  <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                    <Box
                      component="a"
                      href={`${district.facebook}`}
                      target="_blank"
                      sx={{
                        fontWeight: 600,
                        ml: 1,
                        color: "#1976d2",
                        textDecoration: "none",
                      }}
                    >
                      <Facebook />
                    </Box>
                  </Typography>
                )}

                {district.instagram && (
                  <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                    <Box
                      component="a"
                      href={`${district.instagram}`}
                      target="_blank"
                      sx={{
                        fontWeight: 600,
                        ml: 1,
                        color: "#1976d2",
                        textDecoration: "none",
                      }}
                    >
                      <Instagram />
                    </Box>
                  </Typography>
                )}
              </Box>
            </Box>

            <Grid container spacing={3}>
              {/* ВЕРХНИЕ КАРТЫ */}
              <Grid item xs={12} sm={6} md={4}>
                <StaticBlock title={t("mainInfo")}>
                  <DataRow label={t("territory")} value={district.territory} />
                  <DataRow
                    label={t("adminCenter")}
                    value={getLangVal(district, "administrative_center")}
                  />
                  <DataRow label={t("altitude")} value={district.altitude} />
                  <DataRow
                    label={t("population")}
                    value={district.population}
                  />
                  <DataRow
                    label={t("settlementsCount")}
                    value={district.settlements_count}
                  />
                  <DataRow
                    label={t("approvedBudget")}
                    value={
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "green",
                          }}
                        >
                          {district.approved_budget}{" "}
                          <span style={{ color: "black" }}>
                            / {district.year} {t("year")}
                          </span>
                        </Typography>
                      </Box>
                    }
                  />
                </StaticBlock>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StaticBlock title={t("hunan_resources_portent")}>
                  <DataRow2
                    label={t("staffCount")}
                    value={district.staff_count}
                  />

                  <DataRow
                    label={t("municipal_servants_count")}
                    value={
                      <Box sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                          {district.municipal_servants_count}
                        </Typography>
                      </Box>
                    }
                  />

                  <DataRow2
                    label={t("deputiesCount")}
                    value={
                      <Box sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                          {district.deputies_count}
                        </Typography>
                      </Box>
                    }
                  />

                  <DataRow2
                    label={t("women_deputies_count")}
                    value={
                      <Box sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                          {district.women_deputies_count}
                        </Typography>
                      </Box>
                    }
                  />
                  <DataRow2
                    label={t("youth_deputies_count")}
                    value={
                      <Box sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                          {district.youth_deputies_count}
                        </Typography>
                      </Box>
                    }
                  />

                  <DataRow
                    label={t("disabled_deputies_count")}
                    value={
                      <Box sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                          {district.disabled_deputies_count}
                        </Typography>
                      </Box>
                    }
                  />
                </StaticBlock>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StaticBlock title={t("munisipal_organ")}>
                  <DataRow
                    label={t("municipal_enterprises")}
                    value={district.municipal_enterprises}
                  />

                  <DataRow
                    label={t("municipal_institutions")}
                    value={district.municipal_institutions}
                  />
                </StaticBlock>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* РУКОВОДИТЕЛИ */}
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#1a237e",
                    mb: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    fontSize: "20px",
                  }}
                >
                  {t("officials")}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(260px, 1fr))",
                    gap: 3,
                  }}
                >
                  {district.officials?.map((person) => (
                    <Paper
                      key={person.id}
                      elevation={0}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "#fff",
                        borderRadius: 3,
                        border: "1px solid #e0e0e0",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          pt: "100%",
                          bgcolor: "#f5f5f5",
                        }}
                      >
                        <Box
                          component="img"
                          src={person.photo1}
                          alt={person.full_name}
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                      <Box sx={{ p: 2.5, textAlign: "center", flexGrow: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 800, color: "#1a237e", mb: 1 }}
                        >
                          {person.full_name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mb: 2, fontSize: "16px" }}
                        >
                          <strong>{t("position")}:</strong>
                          <br />
                          {getLangVal(person, "position")}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Stack spacing={1} alignItems="center">
                          <Box
                            component="a"
                            href={`tel:${person.phone}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              color: "primary.main",
                              textDecoration: "none",
                              fontSize: "16px",
                              fontWeight: 700,
                            }}
                          >
                            <PhoneIcon sx={{ fontSize: 16 }} /> {person.phone}
                          </Box>
                          {person.whatsapp && (
                            <Box
                              component="a"
                              href={person.whatsapp}
                              target="_blank"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                color: "#2e7d32",
                                textDecoration: "none",
                                fontSize: "0.8rem",
                                fontWeight: 600,
                              }}
                            >
                              <WhatsAppIcon sx={{ fontSize: 16 }} />{" "}
                              {t("writeWhatsapp")}
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {district.economy && (
                <Grid item xs={12} sm={6} md={4}>
                  <StaticBlock title={t("sector_pita")}>
                    <DataRow
                      label={t("cafes_restaurants")}
                      value={district.economy.cafes_restaurants}
                    />

                    <DataRow
                      label={t("canteens")}
                      value={district.economy.canteens}
                    />

                    <DataRow
                      label={t("fast_food")}
                      value={district.economy.fast_food}
                    />
                  </StaticBlock>
                </Grid>
              )}

              {district.economy && (
                <Grid item xs={12} sm={6} md={4}>
                  <StaticBlock title={t("shop_totch")}>
                    <DataRow
                      label={t("shops_2")}
                      value={district.economy.shops}
                    />

                    <DataRow
                      label={t("malls")}
                      value={district.economy.malls}
                    />

                    <DataRow
                      label={t("markets")}
                      value={district.economy.markets}
                    />
                  </StaticBlock>
                </Grid>
              )}

              {district.economy && (
                <Grid item xs={12} sm={6} md={4}>
                  <StaticBlock title={t("sfera_fast")}>
                    <DataRow
                      label={t("pharmacies")}
                      value={district.economy.pharmacies}
                    />

                    <DataRow
                      label={t("beauty_salons")}
                      value={district.economy.beauty_salons}
                    />

                    <DataRow
                      label={t("hotels")}
                      value={district.economy.hotels}
                    />
                    <DataRow
                      label={t("auto_services")}
                      value={district.economy.auto_services}
                    />
                    <DataRow
                      label={t("bank_branches")}
                      value={district.economy.bank_branches}
                    />
                    <DataRow
                      label={t("other_services")}
                      value={district.economy.other_services}
                    />
                  </StaticBlock>
                </Grid>
              )}

              {district.industry && (
                <Grid item xs={12} sm={6} md={4}>
                  <StaticBlock title={t("industry")}>
                    <DataRow
                      label={t("factories")}
                      value={district.industry.factories}
                    />
                    <DataRow
                      label={t("industrial_workshops")}
                      value={district.industry.industrial_workshops}
                    />{" "}
                    <DataRow
                      label={t("logistics_centers")}
                      value={district.industry.logistics_centers}
                    />{" "}
                    <DataRow
                      label={t("plants")}
                      value={district.industry.plants}
                    />
                    <DataRow
                      label={t("sewing_workshops")}
                      value={district.industry.sewing_workshops}
                    />
                  </StaticBlock>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {district.agriculture && (
                <Grid item xs={12} sm={6} md={4}>
                  <StaticBlock title={t("zemelny_font")}>
                    <DataRow
                      label={t("agricultural_land")}
                      value={district.agriculture.agricultural_land}
                    />
                    <DataRow
                      label={t("settlement_land")}
                      value={district.agriculture.settlement_land}
                    />
                    <DataRow
                      label={t("industrial_land")}
                      value={district.agriculture.industrial_land}
                    />
                    <DataRow
                      label={t("protected_land")}
                      value={district.agriculture.protected_land}
                    />
                    <DataRow
                      label={t("forest_land")}
                      value={district.agriculture.forest_land}
                    />
                    <DataRow
                      label={t("water_land")}
                      value={district.agriculture.water_land}
                    />
                    <DataRow
                      label={t("reserve_land")}
                      value={district.agriculture.reserve_land}
                    />
                  </StaticBlock>
                </Grid>
              )}

              {district.agriculture && (
                <Grid item xs={12} sm={6} md={4}>
                  <StaticBlock title={t("animal")}>
                    <DataRow
                      label={t("cattle_any")}
                      value={district.agriculture.cattle}
                    />
                    <DataRow
                      label={t("small_cattle")}
                      value={district.agriculture.small_cattle}
                    />
                    <DataRow
                      label={t("horses")}
                      value={district.agriculture.horses}
                    />
                    <DataRow
                      label={t("poultry")}
                      value={district.agriculture.poultry}
                    />
                    <DataRow
                      label={t("other_animals")}
                      value={district.agriculture.other_animals}
                    />
                  </StaticBlock>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* СТАТИСТИКА */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {/* {district.economy && (
                    <Grid item xs={12} sm={6} md={3}>
                      <StaticBlock
                        title={t("economy")}
                        icon={<BusinessCenterIcon fontSize="inherit" />}
                      >
                        <DataRow
                          label={t("municipalEnterprises")}
                          value={district.economy.municipal_enterprises}
                        />
                        <DataRow
                          label={t("arableLand")}
                          value={district.economy.arable_land}
                        />
                        <DataRow
                          label={t("pastures")}
                          value={district.economy.pastures}
                        />
                        <DataRow
                          label={t("gardens")}
                          value={district.economy.gardens}
                        />
                        <DataRow
                          label={t("cattle")}
                          value={district.economy.cattle}
                        />
                        <DataRow
                          label={t("smallCattle")}
                          value={district.economy.small_cattle}
                        />
                      </StaticBlock>
                    </Grid>
                  )} */}

                  {district.education && (
                    <Grid item xs={12} sm={6} md={3}>
                      <StaticBlock title={t("education")}>
                        <DataRow
                          label={t("universities")}
                          value={district.education.universities}
                        />
                        <DataRow
                          label={t("colleges")}
                          value={district.education.colleges}
                        />
                        <DataRow
                          label={t("schools")}
                          value={district.education.schools}
                        />
                        {/* <DataRow
                          label={t("students")}
                          value={district.education.students}
                        /> */}
                        <DataRow
                          label={t("kindergartens")}
                          value={district.education.kindergartens}
                        />
                        {/* <DataRow
                          label={t("children")}
                          value={district.education.children}
                        /> */}
                        <DataRow
                          label={t("lycees")}
                          value={district.education.lycees}
                        />
                      </StaticBlock>
                    </Grid>
                  )}

                  {district.healthcare && (
                    <Grid item xs={12} sm={6} md={3}>
                      <StaticBlock title={t("healthcare")}>
                        <DataRow
                          label={t("hospitals")}
                          value={district.healthcare.hospitals}
                        />
                        <DataRow
                          label={t("csm")}
                          value={district.healthcare.csm}
                        />
                        <DataRow
                          label={t("faps")}
                          value={district.healthcare.faps}
                        />
                      </StaticBlock>
                    </Grid>
                  )}

                  {district.culture_sport && (
                    <Grid item xs={12} sm={6} md={3}>
                      <StaticBlock title={t("cultureSport")}>
                        <DataRow
                          label={t("cultureHouses")}
                          value={district.culture_sport.culture_houses}
                        />
                        <DataRow
                          label={t("clubs")}
                          value={district.culture_sport.clubs}
                        />
                        <DataRow
                          label={t("museums")}
                          value={district.culture_sport.museums}
                        />
                        <DataRow
                          label={t("libraries")}
                          value={district.culture_sport.libraries}
                        />
                        <DataRow
                          label={t("parks")}
                          value={district.culture_sport.parks}
                        />
                        <DataRow
                          label={t("stadiums")}
                          value={district.culture_sport.stadiums}
                        />
                        <DataRow
                          label={t("gyms")}
                          value={district.culture_sport.gyms}
                        />
                        <DataRow
                          label={t("sportFields")}
                          value={district.culture_sport.sport_fields}
                        />
                      </StaticBlock>
                    </Grid>
                  )}

                  {district.public_organization && (
                    <Grid item xs={12} sm={6} md={3}>
                      <StaticBlock title={t("PublicOrganization")}>
                        <DataRow
                          label={t("aksakal_courts")}
                          value={district.public_organization.aksakal_courts}
                        />
                        <DataRow
                          label={t("aksakal_councils")}
                          value={district.public_organization.aksakal_councils}
                        />
                        <DataRow
                          label={t("women_councils")}
                          value={district.public_organization.women_councils}
                        />
                        <DataRow
                          label={t("youth_councils")}
                          value={district.public_organization.youth_councils}
                        />
                        <DataRow
                          label={t("npos")}
                          value={district.public_organization.npos}
                        />
                        <DataRow
                          label={t("tos")}
                          value={district.public_organization.tos}
                        />
                        <DataRow
                          label={t("jamaats")}
                          value={district.public_organization.jamaats}
                        />
                      </StaticBlock>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Paper>
      </Container>
    </Box>
  );
};

// --- ВСПОМОГАТЕЛЬНЫЕ КОМПОНЕНТЫ ---

const StaticBlock = ({ title, icon, children }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 3,
      border: "1px solid #eee",
      height: "100%",
      bgcolor: "#fafafa",
    }}
  >
    {title && (
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "18px",
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          color: "black",
          textTransform: "uppercase",
        }}
      >
        {icon} {title}
      </Typography>
    )}
    <Stack spacing={0.5}>{children}</Stack>
  </Box>
);

const DataRow = ({ label, value, color, bold, icon }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      borderBottom: "1px dashed #616161",
      pb: 0.2,
    }}
  >
    <Typography
      sx={{ fontSize: "16px", display: "flex", alignItems: "center", gap: 0.5 }}
      color="black"
      fontWeight={600}
    >
      {icon && (
        <Box component="span" sx={{ fontSize: "0.9rem" }}>
          {icon}
        </Box>
      )}
      {label}:
    </Typography>
    {typeof value === "object" && value !== null ? (
      value
    ) : (
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: bold ? 700 : 600,
          color: color || "text.primary",
        }}
      >
        {value ?? "0"}
      </Typography>
    )}
  </Box>
);

const DataRow2 = ({ label, value, color, bold, icon }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      pb: 0.2,
    }}
  >
    <Typography
      sx={{ fontSize: "16px", display: "flex", alignItems: "center", gap: 0.5 }}
      color="black"
      fontWeight={600}
    >
      {icon && (
        <Box component="span" sx={{ fontSize: "0.9rem" }}>
          {icon}
        </Box>
      )}
      {label}:
    </Typography>
    {typeof value === "object" && value !== null ? (
      value
    ) : (
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: bold ? 700 : 600,
          color: color || "text.primary",
        }}
      >
        {value ?? "0"}
      </Typography>
    )}
  </Box>
);

export default MunitsipalitetId;
